import { motion } from "framer-motion";
import { ArrowRight, Wrench, Cpu, Gauge } from "lucide-react";
import { KIKI } from "@/constants/testIds";
import { useT, useLang } from "@/lib/i18n";

const ICON_MAP = [Wrench, Cpu, Gauge];
const CODES = ["01", "02", "03"];
const TONES = ["dark", "yellow", "dark"];
const IDS = [KIKI.programmeCard1, KIKI.programmeCard2, KIKI.programmeCard3];

export function ProgrammesQuick({ onEnquire }) {
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";
  const progs = [t("progQ.p1"), t("progQ.p2"), t("progQ.p3")];

  return (
    <section id="programmes" data-testid={KIKI.programmes} className="relative py-12 md:py-16">
      <div className="max-w-[90rem] mx-auto px-6 md:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-[#FFCC00]" />
            <span className={`label-overline text-muted-foreground ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("progQ.overline")}</span>
          </div>
          <span className={`text-sm text-muted-foreground ${isHi ? "font-hindi" : ""}`}>{t("progQ.kicker")}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {progs.map((p, i) => {
            const Icon = ICON_MAP[i];
            const isYellow = TONES[i] === "yellow";
            const tone = isYellow ? "bg-[#FFCC00] text-[#050505]" : "bg-[#0A0A0A] text-white";
            return (
              <motion.article
                key={IDS[i]}
                data-testid={IDS[i]}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`brochure-card card-hover ${tone} min-h-[190px] flex flex-col justify-between cursor-pointer group`}
                onClick={onEnquire}
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 grid place-items-center rounded-xl bg-white/15 dark:bg-white/15">
                    <Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <span className="font-mono text-2xl font-medium opacity-70">{CODES[i]}</span>
                </div>
                <div>
                  <div className={`label-overline opacity-70 mb-1.5 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{p.stream}</div>
                  <h3 className={`font-extrabold text-2xl md:text-[26px] leading-tight tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}>{p.title}</h3>
                  <p className={`mt-2 text-sm opacity-80 leading-relaxed max-w-[38ch] ${isHi ? "font-hindi" : ""}`}>{p.blurb}</p>
                </div>
                <div className="flex items-center justify-end">
                  <div className={`w-10 h-10 rounded-full grid place-items-center transition-transform group-hover:translate-x-1 ${isYellow ? "bg-[#0A0A0A] text-[#FFCC00]" : "bg-[#FFCC00] text-[#0A0A0A]"}`}>
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
